import { Link as TanStackLink, useRouter as useTanStackRouter } from '@tanstack/react-router'
import { forwardRef } from 'react'

import { resolveTarget, targetToHref } from './target'
import type { LinkComponent, LinkProps } from './types'

/**
 * Public surface of the navigation shim: pages import their navigation
 * primitives from `@/lib/navigation/*` and nowhere else (apps/app/CLAUDE.md),
 * so these type names are part of that contract.
 *
 * @public
 */
export type { LinkProps } from './types'

/**
 * An href no in-app route can own: one with a scheme (https:, mailto:, tel:),
 * a protocol-relative `//host`, or a bare `#fragment`. Resolving those against
 * the current origin would throw away everything but the path, so
 * `https://mercury.com/apply` would navigate to the in-app `/apply` and
 * `#top` to `/`. next/link rendered an ordinary anchor for all of them.
 */
const NON_ROUTE_HREF = /^(?:[a-z][a-z0-9+.-]*:|\/\/|#)/i

/**
 * next/link's surface over TanStack's Link. Outside a RouterProvider (jsdom
 * component tests render pieces of pages with no router) it degrades to a
 * plain anchor, which is what next/link did without a router too.
 */
const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { href, prefetch, replace, scroll, children, ...anchorProps },
  ref,
) {
  const router = useTanStackRouter({ warn: false }) as unknown
  if (typeof href === 'string' && NON_ROUTE_HREF.test(href)) {
    return (
      <a ref={ref} href={href} {...anchorProps}>
        {children}
      </a>
    )
  }
  if (!router) {
    return (
      <a ref={ref} href={targetToHref(href)} {...anchorProps}>
        {children}
      </a>
    )
  }
  const target = resolveTarget(href, window.location.origin)
  return (
    <TanStackLink
      ref={ref}
      to={target.to as never}
      search={target.search as never}
      hash={target.hash}
      replace={replace}
      resetScroll={scroll ?? true}
      preload={prefetch === false ? false : undefined}
      {...(anchorProps as Record<string, unknown>)}
    >
      {children}
    </TanStackLink>
  )
}) as LinkComponent

export default Link
